/**
 * Google Tag Manager Analytics Utility
 * 
 * Provides type-safe analytics tracking with GTM
 */

// Extend Window interface for GTM dataLayer
declare global {
  interface Window {
    dataLayer: any[];
  }
}

interface GTMEvent {
  event: string;
  [key: string]: any;
}

/**
 * Initialize GTM dataLayer if it doesn't exist
 */
export const initDataLayer = () => {
  window.dataLayer = window.dataLayer || [];
};

/**
 * Push event to GTM dataLayer
 */
export const pushToDataLayer = (data: GTMEvent) => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push(data);
  }
};

/**
 * Track page view
 */
export const trackPageView = (pagePath: string, pageTitle?: string) => {
  pushToDataLayer({
    event: 'pageview',
    page: {
      path: pagePath,
      title: pageTitle || document.title,
      location: window.location.href,
    },
  });
  
  // Also push virtualPageview event for GTM to detect SPA navigation
  pushToDataLayer({
    event: 'virtualPageview',
    virtualPageURL: pagePath,
    virtualPageTitle: pageTitle || document.title,
  });
};

/**
 * Track form step completion
 */
export const trackFormStep = (stepNumber: number, stepName: string) => {
  pushToDataLayer({
    event: 'form_step_completed',
    form: {
      step: stepNumber,
      stepName: stepName,
    },
  });
};

/**
 * Track form submission
 */
export const trackFormSubmission = (formName: string, success: boolean) => {
  pushToDataLayer({
    event: success ? 'form_submission_success' : 'form_submission_error',
    form: {
      name: formName,
      success: success,
    },
  });
};

/**
 * Track FCA validation
 */
export const trackFCAValidation = (fcaNumber: string, isValid: boolean) => {
  pushToDataLayer({
    event: 'fca_validation',
    fca: {
      valid: isValid,
      // Don't send actual FCA number for privacy
      hasNumber: !!fcaNumber,
    },
  });
};

/**
 * Track business name check
 */
export const trackBusinessNameCheck = (available: boolean) => {
  pushToDataLayer({
    event: 'business_name_check',
    business: {
      available: available,
    },
  });
};

/**
 * Track DNS subdomain check
 */
export const trackDNSCheck = (available: boolean) => {
  pushToDataLayer({
    event: 'dns_check',
    dns: {
      available: available,
    },
  });
};

/**
 * Track custom event
 */
export const trackCustomEvent = (
  eventName: string,
  eventData?: Record<string, any>
) => {
  pushToDataLayer({
    event: eventName,
    ...eventData,
  });
};

/**
 * Track error
 */
export const trackError = (errorMessage: string, errorContext?: string) => {
  pushToDataLayer({
    event: 'error',
    error: {
      message: errorMessage,
      context: errorContext,
    },
  });
};

/**
 * Check if analytics is enabled
 */
export const isAnalyticsEnabled = (): boolean => {
  return (
    process.env.REACT_APP_ENABLE_ANALYTICS === 'true' &&
    process.env.REACT_APP_ENVIRONMENT === 'production'
  );
};

/**
 * Initialize analytics
 */
export const initializeAnalytics = () => {
  if (isAnalyticsEnabled()) {
    initDataLayer();
    console.log('Analytics initialized');
  }
};
